import styled from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/Medical/MedicalInfoStep.styles.js
import { DIET_C } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// MEDICAL INFO STEP
// Reuses the --c-*/--sp-*/--r-sm/--t-fast custom properties that
// AssessmentStepShell.styles.js's <Section> scopes over the whole step —
// this file never redeclares them, same as LifestyleStep.styles.js /
// ActivityLevelStep.styles.js.
// ═══════════════════════════════════════════════════════════════════════════════

export const FieldGroup = styled.div`
  margin-bottom: var(--sp-6);
`;
FieldGroup.displayName = "FieldGroup";

export const FieldLabel = styled.label`
  display: block;
  color: var(--c-text);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: var(--sp-2);
`;
FieldLabel.displayName = "FieldLabel";

export const RequiredMark = styled.span`
  color: ${DIET_C.accent};
  margin-left: 2px;
`;
RequiredMark.displayName = "RequiredMark";

export const HelperText = styled.p`
  margin: var(--sp-2) 0 0;
  color: var(--c-text-muted);
  font-size: 0.8rem;
  line-height: 1.4;
`;
HelperText.displayName = "HelperText";

// ─── Conditions — multi-select checkbox cards ──────────────────────────────
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-3);

  @media (max-width: 60em) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 30em) {
    grid-template-columns: 1fr;
  }
`;
CardGrid.displayName = "CardGrid";

export const ConditionCard = styled.button`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-height: 52px;
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--r-sm);
  background: var(--c-surface);
  color: var(--c-text);
  font-size: 0.9rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  transition: border-color var(--t-fast);

  &:hover {
    border-color: ${DIET_C.primary};
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }
`;
ConditionCard.displayName = "ConditionCard";

export const CheckIcon = styled.span`
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}88`)};
  background: ${({ $active }) => ($active ? DIET_C.primary : "transparent")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.7rem;
  line-height: 1;
`;
CheckIcon.displayName = "CheckIcon";

// ─── Current medications — free text ────────────────────────────────────────
export const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  border-radius: var(--r-sm);
  border: 1px solid ${DIET_C.secondary}88;
  background: var(--c-surface);
  color: var(--c-text);
  padding: 12px 14px;
  font-size: 0.92rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color var(--t-fast);

  &:focus {
    outline: none;
    border-color: ${DIET_C.primary};
    box-shadow: 0 0 0 3px ${DIET_C.primary}22;
  }

  &::placeholder {
    color: var(--c-text-muted);
  }
`;
TextArea.displayName = "TextArea";

export const CharCountRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--sp-1);
`;
CharCountRow.displayName = "CharCountRow";

export const CharCount = styled.span`
  font-size: 0.78rem;
  color: ${({ $over }) => ($over ? DIET_C.error : "var(--c-text-muted)")};
`;
CharCount.displayName = "CharCount";

// ─── Risk notice — 2+ severe conditions selected (see UC-08) ───────────────
export const RiskNotice = styled.div`
  background: ${DIET_C.accent}14;
  border: 1px solid ${DIET_C.accent}55;
  border-radius: var(--r-sm);
  padding: var(--sp-4) var(--sp-5);
  color: var(--c-text);
  font-size: 0.88rem;
  line-height: 1.5;
`;
RiskNotice.displayName = "RiskNotice";

// ─── Plan-block notice — eating-disorder concern selected (DEC-17) ────────
// Deliberately calmer/more supportive than RiskNotice: this isn't a
// warning about the plan being riskier, it's a message that the
// personalized weight-loss engine will be skipped in favor of a safe,
// supportive next step. Uses the neutral surface + primary accent rather
// than DIET_C.accent's alert-style tone.
export const SupportNotice = styled.div`
  background: var(--c-surface);
  border: 1px solid ${DIET_C.primary}55;
  border-left: 4px solid ${DIET_C.primary};
  border-radius: var(--r-sm);
  padding: var(--sp-4) var(--sp-5);
  color: var(--c-text);
  font-size: 0.88rem;
  line-height: 1.5;
`;
SupportNotice.displayName = "SupportNotice";